/**
 * UUID Generator Component
 */
import React, { useState } from 'react';
import { Copy, QrCode } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const UUIDGenerator = ({ onGenerate }) => {
  const [uuidCount, setUuidCount] = useState(5);
  const [uuidPrefix, setUuidPrefix] = useState('');
  const [qrEnabled, setQrEnabled] = useState(true);
  const [generatedUuids, setGeneratedUuids] = useState([]);
  const [generatedBatch, setGeneratedBatch] = useState('');
  const [loading, setLoading] = useState(false);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const generateUuids = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/generate-uuid`, {
        count: uuidCount,
        prefix: uuidPrefix,
        qr_enabled: qrEnabled
      });
      setGeneratedUuids(response.data.uuids);
      setGeneratedBatch(response.data.batch);
      toast.success(`Generated ${response.data.count} UUIDs${!qrEnabled ? ' (QR disabled)' : ''}`);
      if (onGenerate) onGenerate();
    } catch (error) {
      toast.error('Failed to generate UUIDs');
    } finally {
      setLoading(false);
    }
  };

  const copyAllUuids = () => {
    navigator.clipboard.writeText(generatedUuids.join('\n'));
    toast.success('All UUIDs copied to clipboard');
  };

  return (
    <div className="max-w-xl mx-auto">
      <div className="rounded-3xl border border-border/40 bg-card/50 p-8">
        <h2 className="text-2xl font-bold mb-6">Generate UUIDs</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Number of UUIDs</label>
            <input
              type="number"
              value={uuidCount}
              onChange={(e) => setUuidCount(Math.min(100, Math.max(1, parseInt(e.target.value) || 1)))}
              className="w-full bg-secondary rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary"
              min="1"
              max="100"
              data-testid="uuid-count-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Prefix (optional)</label>
            <input
              type="text"
              value={uuidPrefix}
              onChange={(e) => setUuidPrefix(e.target.value)}
              placeholder="e.g., wedding-2024"
              className="w-full bg-secondary rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="uuid-prefix-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Tags will be created as: {uuidPrefix ? `${uuidPrefix}-` : ''}[uuid]
            </p>
          </div>
          
          {/* QR Code Toggle */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-secondary/50 border border-border/50">
            <div className="flex items-center gap-3">
              <QrCode className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="font-medium text-sm">Enable QR Code</p>
                <p className="text-xs text-muted-foreground">Allow users to download QR codes for these tags</p>
              </div>
            </div>
            <button
              onClick={() => setQrEnabled(!qrEnabled)}
              className={`w-12 h-6 rounded-full transition-all ${
                qrEnabled ? 'bg-primary' : 'bg-secondary border border-border'
              }`}
              data-testid="uuid-qr-toggle"
            >
              <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                qrEnabled ? 'translate-x-6' : 'translate-x-0.5'
              }`} />
            </button>
          </div>
          
          <button
            onClick={generateUuids}
            disabled={loading}
            className="w-full bg-primary text-primary-foreground rounded-full py-3 font-medium hover:scale-105 transition-all disabled:opacity-50"
            data-testid="generate-uuid-btn"
          >
            {loading ? 'Generating...' : 'Generate UUIDs'}
          </button>
        </div>

        {generatedUuids.length > 0 && (
          <div className="mt-6 p-4 bg-secondary/30 rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <span className="font-medium">Generated {generatedUuids.length} UUIDs</span>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Batch: {generatedBatch}</span>
                <button
                  onClick={copyAllUuids}
                  className="p-2 hover:bg-secondary rounded-lg text-xs font-medium flex items-center gap-1"
                  title="Copy all"
                >
                  <Copy className="w-3 h-3" />
                  Copy All
                </button>
              </div>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {generatedUuids.map(uuid => (
                <div key={uuid} className="flex items-center gap-2 bg-background rounded-lg p-2">
                  <code className="flex-1 text-sm truncate">{uuid}</code>
                  <button 
                    onClick={() => copyToClipboard(uuid)} 
                    className="p-1 hover:bg-secondary rounded"
                    title="Copy"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UUIDGenerator;
